import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface ProductIngredient {
  id: string;
  product_id: string;
  ingredient_id: string;
  quantity: number;
  created_at: string;
  ingredient?: {
    id: string;
    name: string;
    unit: string;
    current_stock: number;
    cost_per_unit: number;
  };
}

export function useProductIngredients(productId?: string) {
  const { activeRestaurant } = useRestaurantContext();
  const [productIngredients, setProductIngredients] = useState<ProductIngredient[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProductIngredients = useCallback(async (prodId?: string) => {
    const id = prodId || productId;
    if (!id || !activeRestaurant) {
      setProductIngredients([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("product_ingredients" as any)
        .select(`
          *,
          ingredient:ingredients(id, name, unit, current_stock, cost_per_unit)
        `)
        .eq("product_id", id);

      if (error) throw error;
      setProductIngredients((data as unknown as ProductIngredient[]) || []);
    } catch (error) {
      console.error("Error fetching product ingredients:", error);
      toast.error("Erro ao carregar ingredientes do produto");
    } finally {
      setIsLoading(false);
    }
  }, [productId, activeRestaurant]);

  const addProductIngredient = async (
    prodId: string,
    ingredientId: string,
    quantity: number
  ) => {
    try {
      // Check if ingredient already exists for this product
      const existing = productIngredients.find(
        (pi) => pi.ingredient_id === ingredientId
      );
      
      if (existing) {
        // Update quantity instead
        return updateProductIngredient(existing.id, quantity + existing.quantity);
      }

      const { data, error } = await supabase
        .from("product_ingredients" as any)
        .insert({
          product_id: prodId,
          ingredient_id: ingredientId,
          quantity,
        })
        .select(`
          *,
          ingredient:ingredients(id, name, unit, current_stock, cost_per_unit)
        `)
        .single();

      if (error) throw error;

      const newIngredient = data as unknown as ProductIngredient;
      setProductIngredients((prev) => [...prev, newIngredient]);
      toast.success("Ingrediente adicionado à receita!");
      return true;
    } catch (error) {
      console.error("Error adding product ingredient:", error);
      toast.error("Erro ao adicionar ingrediente");
      return false;
    }
  };

  const updateProductIngredient = async (id: string, quantity: number) => {
    try {
      const { error } = await supabase
        .from("product_ingredients" as any)
        .update({ quantity })
        .eq("id", id);

      if (error) throw error;

      setProductIngredients((prev) =>
        prev.map((pi) => (pi.id === id ? { ...pi, quantity } : pi))
      );
      toast.success("Quantidade atualizada!");
      return true;
    } catch (error) {
      console.error("Error updating product ingredient:", error);
      toast.error("Erro ao atualizar quantidade");
      return false;
    }
  };

  const removeProductIngredient = async (id: string) => {
    try {
      const { error } = await supabase
        .from("product_ingredients" as any)
        .delete()
        .eq("id", id);

      if (error) throw error;

      setProductIngredients((prev) => prev.filter((pi) => pi.id !== id));
      toast.success("Ingrediente removido da receita!");
      return true;
    } catch (error) {
      console.error("Error removing product ingredient:", error);
      toast.error("Erro ao remover ingrediente");
      return false;
    }
  };

  const calculateRecipeCost = () => {
    return productIngredients.reduce((total, pi) => {
      const costPerUnit = pi.ingredient?.cost_per_unit || 0;
      return total + costPerUnit * pi.quantity;
    }, 0);
  };

  return {
    productIngredients,
    isLoading,
    fetchProductIngredients,
    addProductIngredient,
    updateProductIngredient,
    removeProductIngredient,
    calculateRecipeCost,
  };
}
